import { getVATPageUrl } from '@constants'
import { isVATObliged, useBusinessContext } from '@context'
import { DashboardVatPeriodKind, DashboardVatPeriodState, IDashboardVatPeriod } from '@query'
import { formatDate } from '@utils'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { FaCheck, FaClock, FaLock, FaMinus, FaPen } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import styled, { DefaultTheme } from 'styled-components'
import { useDashboardOverview } from '../hooks/useDashboardOverview'
import {
  VAT_MONTH_TRACKS,
  VAT_MONTH_TRACKS_NARROW,
  VAT_MONTH_TRACKS_STACKED,
  WHEN_NARROW,
  WHEN_STACKED
} from '../layout'
import { Card, CardHeaderMeta } from './shared/Card'

const STATE_ICONS: Record<DashboardVatPeriodState, React.ReactNode> = {
  reported: <FaCheck />,
  locked: <FaLock />,
  open: <FaPen />,
  ongoing: <FaClock />,
  future: <FaMinus />,
  missing: <FaMinus />
}

const isFilingChip = (state: DashboardVatPeriodState) =>
  state === 'open' || state === 'locked' || state === 'ongoing'

const vatChrome = (theme: DefaultTheme, state: DashboardVatPeriodState) => {
  switch (state) {
    case 'reported':
      return {
        fill: theme.dashboard.positiveTint,
        ink: theme.dashboard.positive,
        status: theme.colors.iconGray
      }
    case 'locked':
      return {
        fill: theme.colors.menuBg,
        ink: theme.colors.neutralWhite,
        status: theme.colors.menuBg
      }
    case 'open':
      return {
        fill: theme.dashboard.cautionTint,
        ink: theme.dashboard.caution,
        status: theme.dashboard.caution
      }
    case 'ongoing':
      return {
        fill: theme.dashboard.infoTint,
        ink: theme.colors.primary,
        status: theme.colors.primary
      }
    case 'future':
    case 'missing':
      return {
        fill: theme.colors.neutralGray,
        ink: theme.colors.iconGray,
        status: theme.dashboard.onNavyMuted
      }
    default: {
      const exhaustive: never = state
      return exhaustive
    }
  }
}

export const FinancialYearCard: React.FC = () => {
  const [t] = useTranslation()
  const { slug, data: business } = useBusinessContext()
  const { data: overview } = useDashboardOverview()
  const [selectedStart, setSelectedStart] = useState<string | null>(null)

  if (!isVATObliged(business) || !overview?.vat_year) return null

  const vatYear = overview.vat_year
  const periods = vatYear.vat_periods ?? []
  const currentStart = periods.find(period => period.current)?.start_date
  const selected =
    periods.find(period => period.start_date === (selectedStart ?? currentStart)) ?? periods[0]
  const progress =
    vatYear.period_count > 0 ? Math.round((vatYear.reported_count / vatYear.period_count) * 100) : 0
  const yearLabel = vatYear.year
  const showPeriodChips = periods.length > 1

  return (
    <FullWidthCard
      title={t('dashboard.vatYear.title', { year: yearLabel })}
      tooltip={t('dashboard.vatYear.tooltip')}
      headerMeta={
        <CardHeaderMeta>
          {t('dashboard.vatYear.progress', {
            reported: vatYear.reported_count,
            total: vatYear.period_count,
            percent: progress
          })}
        </CardHeaderMeta>
      }
    >
      {showPeriodChips && (
        <MonthStrip>
          {periods.map(period => (
            <MonthChip
              key={period.start_date}
              type="button"
              $state={period.state}
              $selected={period.start_date === selected?.start_date}
              $span={spanMonths(period)}
              onClick={() => setSelectedStart(period.start_date)}
              title={t(vatStateLabelKey(period))}
            >
              <MonthDot $state={period.state}>{STATE_ICONS[period.state]}</MonthDot>
              <MonthLabel $state={period.state}>{chipLabel(period)}</MonthLabel>
              <MonthStatus $state={period.state}>{t(vatStateLabelKey(period))}</MonthStatus>
            </MonthChip>
          ))}
        </MonthStrip>
      )}

      {selected && (
        <SelectedStrip $solo={!showPeriodChips}>
          <SelectedIcon $state={selected.state}>{STATE_ICONS[selected.state]}</SelectedIcon>
          <SelectedText>
            <SelectedTitle>
              {t('dashboard.vatYear.selected.title', {
                range: formatRange(selected),
                state: t(vatStateLabelKey(selected)).toLowerCase()
              })}
            </SelectedTitle>
            <SelectedDetail>{selectedDetail(selected, t)}</SelectedDetail>
          </SelectedText>
          {selected.id != null && (
            <SelectedPrimary to={getVATPageUrl(slug, { id: selected.id })}>
              {t('dashboard.vatYear.selected.primary')}
            </SelectedPrimary>
          )}
        </SelectedStrip>
      )}
    </FullWidthCard>
  )
}

const FullWidthCard = styled(Card)`
  flex: 1;
  width: 100%;
  min-width: 0;
`

const formatRange = (period: IDashboardVatPeriod) =>
  `${formatDate(period.start_date)} – ${formatDate(period.end_date)}`

const vatStateLabelKey = (period: IDashboardVatPeriod) => {
  if (period.placeholder && period.state === 'future') {
    return 'dashboard.vatYear.state.placeholder'
  }
  return `dashboard.vatYear.state.${period.state}`
}

const spanMonths = (period: IDashboardVatPeriod) => {
  const start = dayjs(period.start_date).startOf('month')
  const end = dayjs(period.end_date).startOf('month')
  return Math.max(1, end.diff(start, 'month') + 1)
}

const chipLabel = (period: IDashboardVatPeriod) => {
  const start = dayjs(period.start_date)
  const kind: DashboardVatPeriodKind = period.period
  switch (kind) {
    case 'YEAR':
      return start.format('YYYY')
    case 'QUARTER':
      return `Q${Math.floor(start.month() / 3) + 1}`
    case 'MONTH':
      return start.format('MMM')
    default: {
      const exhaustive: never = kind
      return exhaustive
    }
  }
}

const selectedDetail = (period: IDashboardVatPeriod, t: TFunction) => {
  if (period.placeholder && period.state === 'future') {
    return t('dashboard.vatYear.selected.detail.placeholder', {
      due: period.due_date ? formatDate(period.due_date) : '—'
    })
  }
  if (period.placeholder) {
    return t('dashboard.vatYear.selected.detail.missing', {
      due: period.due_date ? formatDate(period.due_date) : '—'
    })
  }
  if (period.state === 'open' || period.state === 'locked' || period.state === 'ongoing') {
    return t(`dashboard.vatYear.selected.detail.${period.state}`, {
      due: period.due_date ? formatDate(period.due_date) : '—'
    })
  }
  return t(`dashboard.vatYear.selected.detail.${period.state}`)
}

const MonthStrip = styled.div`
  --vat-tracks: ${VAT_MONTH_TRACKS};
  display: grid;
  grid-template-columns: repeat(var(--vat-tracks), minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem ${({ theme }) => theme.spacing.lg}rem
    ${({ theme }) => theme.spacing.md}rem;

  ${WHEN_STACKED} {
    --vat-tracks: ${VAT_MONTH_TRACKS_STACKED};
  }

  ${WHEN_NARROW} {
    --vat-tracks: ${VAT_MONTH_TRACKS_NARROW};
  }
`

const MonthChip = styled.button<{
  $state: DashboardVatPeriodState
  $selected: boolean
  $span: number
}>`
  display: flex;
  grid-column: span min(${({ $span }) => $span}, var(--vat-tracks));
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  box-sizing: border-box;
  min-height: 6.5rem;
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.xs}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  border: 1px solid
    ${({ theme, $state }) => (isFilingChip($state) ? theme.colors.menuBg : theme.colors.borderSoft)};
  background: ${({ theme, $state }) =>
    isFilingChip($state) ? theme.dashboard.surfaceSubtle : theme.colors.neutralWhite};
  outline: ${({ theme, $selected }) => ($selected ? `2px solid ${theme.colors.primary}` : 'none')};
  outline-offset: 1px;
  font-family: inherit;
  cursor: pointer;

  &:hover {
    filter: brightness(97%);
  }
`

const MonthDot = styled.span<{ $state: DashboardVatPeriodState }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.625rem;
  height: 1.625rem;
  border-radius: 50%;
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  background: ${({ theme, $state }) => vatChrome(theme, $state).fill};
  color: ${({ theme, $state }) => vatChrome(theme, $state).ink};
`

const MonthLabel = styled.span<{ $state: DashboardVatPeriodState }>`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: ${({ $state }) => (isFilingChip($state) ? 700 : 500)};
  color: ${({ theme, $state }) =>
    $state === 'future' || $state === 'missing' ? theme.colors.iconGray : theme.colors.metalGray};
`

const MonthStatus = styled.span<{ $state: DashboardVatPeriodState }>`
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  color: ${({ theme, $state }) => vatChrome(theme, $state).status};
`

const SelectedStrip = styled.div<{ $solo?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
  margin: ${({ theme, $solo }) =>
    $solo ? `${theme.spacing.lg}rem` : `0 ${theme.spacing.lg}rem ${theme.spacing.lg}rem`};
  padding: ${({ theme }) => theme.spacing.md}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.dashboard.surfaceSubtle};
`

const SelectedIcon = styled.div<{ $state: DashboardVatPeriodState }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2.125rem;
  height: 2.125rem;
  border-radius: 0.6875rem;
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
  background: ${({ theme, $state }) => vatChrome(theme, $state).fill};
  color: ${({ theme, $state }) =>
    $state === 'future' || $state === 'missing'
      ? theme.dashboard.textSubtle
      : vatChrome(theme, $state).ink};
`

const SelectedText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxxs}rem;
  flex: 1;
  min-width: 0;
`

const SelectedTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.menuBg};
`

const SelectedDetail = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.dashboard.textSubtle};
`

const SelectedPrimary = styled(Link)`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme }) => theme.colors.menuBg};
  color: ${({ theme }) => theme.colors.neutralWhite};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  white-space: nowrap;
  text-decoration: none;

  &:hover {
    filter: brightness(88%);
    color: ${({ theme }) => theme.colors.neutralWhite};
    text-decoration: none;
  }
`
